import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { featureSections } from '../../mocks/featuresData';
import { Link } from 'react-router-dom';
import FeatureCard from './components/FeatureCard';
import BrokerCoverageChart from './components/BrokerCoverageChart';
import ResponseTimeChart from './components/ResponseTimeChart';

export default function Features() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at 50% 0%, rgba(59,130,246,0.08), transparent 55%)',
            }}
          />
        </div>
        <div className="relative w-full px-6 md:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-5 leading-tight">
              Everything you need to search loads
            </h1>
            <p className="text-lg md:text-xl text-zinc-500 leading-relaxed max-w-2xl mx-auto">
              Four brokers. One query. Real-time results, deep links, and fleet
              controls — no switching portals, no made-up features.
            </p>
          </div>
        </div>
      </section>

      {/* Platform Overview Image */}
      <section className="pb-12 md:pb-16 bg-white">
        <div className="w-full px-6 md:px-10">
          <div className="max-w-5xl mx-auto">
            <div className="w-full h-[300px] md:h-[400px] rounded-[18px] overflow-hidden border border-zinc-200">
              <img
                src="/assets/images/platform-overview.jpg"
                alt="LoadAPI aggregates searches from Echo, CH Robinson, Uber Freight, and RXO into one unified platform"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Feature Sections */}
      <main className="pb-20 md:pb-28">
        {featureSections.map((section, sectionIndex) => (
          <section
            key={section.id}
            className={`py-16 md:py-24 ${
              sectionIndex % 2 === 1 ? 'bg-zinc-50/60' : 'bg-white'
            }`}
          >
            <div className="w-full px-6 md:px-10">
              <div className="max-w-6xl mx-auto">
                {/* Section Header */}
                <div className="mb-12 md:mb-16">
                  <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 mb-3">
                    {section.heading}
                  </h2>
                  <p className="text-zinc-500 text-base md:text-lg max-w-xl">
                    {section.subheading}
                  </p>
                </div>

                {/* Search & Coverage: chart above, cards below */}
                {section.id === 'search' && (
                  <>
                    <div className="mb-10 md:mb-14">
                      <BrokerCoverageChart />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {section.features.map((feature) => (
                        <FeatureCard key={feature.id} feature={feature} />
                      ))}
                    </div>
                  </>
                )}

                {/* Book & Run: cards left, image right */}
                {section.id === 'booking' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 content-start">
                      {section.features.map((feature) => (
                        <FeatureCard key={feature.id} feature={feature} />
                      ))}
                    </div>
                    <div className="flex items-center justify-center">
                      <div className="w-full h-[280px] md:h-[360px] rounded-[18px] overflow-hidden border border-zinc-200">
                        <img
                          src="/assets/images/booking-workflow.jpg"
                          alt="Freight booking workflow from search to deep-link booking confirmation"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Built for Fleets: image left, cards right */}
                {section.id === 'fleets' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="flex items-center justify-center order-2 lg:order-1">
                      <div className="w-full h-[280px] md:h-[360px] rounded-[18px] overflow-hidden border border-zinc-200">
                        <img
                          src="/assets/images/fleet-admin-dashboard.jpg"
                          alt="Fleet admin dashboard with multi-user company accounts and permission controls"
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 content-start order-1 lg:order-2">
                      {section.features.map((feature) => (
                        <FeatureCard key={feature.id} feature={feature} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Performance: card left, chart right */}
                {section.id === 'performance' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="grid grid-cols-1 gap-6 content-start">
                      {section.features.map((feature) => (
                        <FeatureCard key={feature.id} feature={feature} />
                      ))}
                    </div>
                    <div className="flex items-center justify-center">
                      <ResponseTimeChart />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        ))}
      </main>

      {/* CTA Banner */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at 50% 0%, rgba(59,130,246,0.08), transparent 55%)',
            }}
          />
        </div>
        <div className="relative w-full px-6 md:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-5">
              Stop logging into four portals
            </h2>
            <p className="text-zinc-500 text-lg mb-8 max-w-xl mx-auto">
              Connect your broker accounts and start searching from one place
              today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/join"
                className="px-7 py-3 bg-blue-600 text-white text-sm font-semibold rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Get Started
              </Link>
              <Link
                to="/"
                className="px-7 py-3 bg-zinc-100 text-zinc-700 text-sm font-semibold rounded-full hover:bg-zinc-200 transition-colors whitespace-nowrap"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
